import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { trackSessionStart, trackFirstMessageSent, trackSessionEnd } from 'tripPlanner/utils/chatAnalytics'
import * as React from 'react'
import noop from 'lib/function/noop'
import ChatLandingPage from '../ChatLandingPage/ChatLandingPage'
import TripPlannerHeader from '../TripPlannerHeader'
import PaneResizer from 'tripPlanner/components/PaneResizer'
import { clamp } from 'lib/maths/mathUtils'
import {
  MainLayout,
  ContentWrapper,
  MainContent,
  ChatColumnLayout,
  LandingPageWrapper,
  CHAT_DEFAULT_WIDTH, CHAT_MAX_WIDTH, CHAT_MIN_WIDTH,
  LEFT_PANE_WIDTH_VAR,
  RIGHT_PANEL_MIN_WIDTH,
} from './styles/layoutStyles'
import { DrawerAskAIProvider } from './contexts/DrawerAskAIContext'
import { useTripPlannerEventBus } from './contexts/TripPlannerEventBus'
import { useChatActions } from './contexts/ChatActionsContext'
import { useChatStreamStatus } from './contexts/ChatStreamStatusContext'
import { useChatStreamMessages } from './contexts/ChatStreamMessagesContext'
import { MapInteractionProvider } from './contexts/MapInteractionContext'
import type { ChatNavigationOptions } from './hooks/useChatNavigation'
import { useMessageContext } from './hooks/useMessageContext'
import ChatContainer from './containers/ChatContainer'
import RightPanelContainer from './containers/RightPanelContainer'
import DrawerContainer from './containers/DrawerContainer'
import {
  CLASSIC_TRIP_MIGRATION_QUERY_PARAM,
  consumeClassicTripMigrationPrompt,
} from 'tripPlanner/utils/classicTripMigration'

// ── Props interface ──

interface Props {
  chatId: number | undefined
  syncChatId: (id: number | undefined, options?: ChatNavigationOptions) => void
  isNewChatRoute: boolean
  isLandingRoute: boolean
  existingRouteChatId: number | undefined
  navigateToNewChatRoute: () => void
  isDevMode: boolean
  isSpoofed: boolean
  spoofingAgentEmail?: string
  onDevModeToggle: () => void
  setBookingSelection: React.Dispatch<React.SetStateAction<{
    orderIds: Array<string>
    itemIds: Array<string>
  }>>
  selectedBookingItemIds?: Array<string>
  prefilledOrderChipLabels: Array<string>
  onRemovePrefilledOrderTag: (tag: string) => void
}

const DEFAULT_SELECTED_BOOKING_ITEM_IDS: Array<string> = []

// ── Orchestrator: reads from contexts, wires containers ──

function ChatPageContent({
  chatId,
  syncChatId,
  isNewChatRoute,
  isLandingRoute,
  existingRouteChatId,
  navigateToNewChatRoute,
  isDevMode,
  isSpoofed,
  spoofingAgentEmail,
  onDevModeToggle,
  setBookingSelection,
  selectedBookingItemIds = DEFAULT_SELECTED_BOOKING_ITEM_IDS,
  prefilledOrderChipLabels,
  onRemovePrefilledOrderTag,
}: Props) {
  const eventBus = useTripPlannerEventBus()
  const { search } = useLocation()

  // ── Read from contexts ──

  const { submitMessage, resetConversation, resetStreamingState, setFollowTail } = useChatActions()
  const { isStreaming, loadingState } = useChatStreamStatus()
  const { messages } = useChatStreamMessages()

  // ── Chat input ref (replaces focus_chat_input event) ──

  const chatInputRef = useRef<HTMLTextAreaElement | null>(null)
  const consumedClassicTripMigrationTokenRef = useRef<string | null>(null)

  // ── Hover state (MapInteractionContext) ──

  const [hoveredHotelId, setHoveredHotelId] = useState<string | undefined>(undefined)
  const isCardHoveredRef = useRef(false)

  const handleMarkerHover = useCallback((markerId: string | undefined) => {
    if (markerId === undefined && isCardHoveredRef.current) return
    setHoveredHotelId(markerId)
  }, [])

  const handleCardHover = useCallback((offerId: string | undefined) => {
    isCardHoveredRef.current = offerId !== undefined
    setHoveredHotelId(offerId)
  }, [])

  const mapInteractionValue = useMemo(() => ({
    onCardHover: handleCardHover,
    onMarkerHover: handleMarkerHover,
    highlightedMarkerId: hoveredHotelId,
  }), [handleCardHover, handleMarkerHover, hoveredHotelId])

  // ── Pane resizer ──

  const mainContentRef = useRef<HTMLDivElement>(null)
  const leftPaneWidth = useRef<number>(CHAT_DEFAULT_WIDTH)

  const handlePaneDragStart = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()

    const handleDragging = (e: MouseEvent) => {
      if (!mainContentRef.current) return
      const rect = mainContentRef.current.getBoundingClientRect()
      const containerWidth = rect.width
      const minLeft = CHAT_MIN_WIDTH
      const maxLeft = Math.max(
        CHAT_MIN_WIDTH,
        Math.min(CHAT_MAX_WIDTH, containerWidth - RIGHT_PANEL_MIN_WIDTH),
      )
      const newWidth = clamp(minLeft, e.clientX - rect.left, maxLeft)
      leftPaneWidth.current = newWidth
      mainContentRef.current.style.setProperty(LEFT_PANE_WIDTH_VAR, `${newWidth}px`)
    }

    const handleDragEnd = () => {
      window.removeEventListener('mousemove', handleDragging)
      window.removeEventListener('mouseup', handleDragEnd)
    }

    window.addEventListener('mousemove', handleDragging)
    window.addEventListener('mouseup', handleDragEnd)
  }, [])

  // ── chatIdRef for async operations ──

  const chatIdRef = useRef(chatId)
  useEffect(() => {
    chatIdRef.current = chatId
  }, [chatId])

  // ── Analytics: session start + first message tracking ──

  const pageLoadTimestamp = useRef<number>(Date.now())
  const firstMessageFired = useRef<boolean>(false)
  const lastTrackedChatId = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (chatId !== lastTrackedChatId.current) {
      lastTrackedChatId.current = chatId
      const sessionType = existingRouteChatId !== undefined ? 'returning' : 'new'
      trackSessionStart(chatId, sessionType)
      firstMessageFired.current = false
      pageLoadTimestamp.current = Date.now()
    }
  }, [chatId, existingRouteChatId])

  useEffect(() => {
    const handlePageHide = () => {
      const durationMs = Date.now() - pageLoadTimestamp.current
      trackSessionEnd(lastTrackedChatId.current, durationMs)
    }
    window.addEventListener('pagehide', handlePageHide)
    return () => window.removeEventListener('pagehide', handlePageHide)
  }, [])

  // ── useMessageContext (stays in orchestrator — needed by both landing page and ChatContainer) ──

  const isLoading = loadingState.status === 'loadingHistory' || loadingState.status === 'preparingStream'

  const focusChatInput = useCallback(() => {
    chatInputRef.current?.focus()
  }, [])

  const {
    activeMultiChoiceSelection,
    mobileSelectionText,
    offerContextTag,
    handleSubmit,
    submitDirect,
    handleMultiChoiceSelectionChange,
    handleRemoveTag,
    resetSelectionState,
  } = useMessageContext({
    streamingSubmit: submitMessage,
    setFollowTail,
    // ChatContainer owns messageInputValue state and ChatMessageInput clears itself
    // on submit. The orchestrator doesn't need a real setter here.
    setMessageInputValue: noop,
    focusChatInput,
    prefilledMultiChoiceSelection: prefilledOrderChipLabels,
  })

  const handleSubmitWithTracking = useCallback((message: string) => {
    if (!firstMessageFired.current && chatId) {
      trackFirstMessageSent(chatId, Date.now() - pageLoadTimestamp.current)
      firstMessageFired.current = true
    }
    return handleSubmit(message)
  }, [handleSubmit, chatId])

  const submitDirectWithTracking = useCallback((message: string) => {
    if (!firstMessageFired.current && chatId) {
      trackFirstMessageSent(chatId, Date.now() - pageLoadTimestamp.current)
      firstMessageFired.current = true
    }
    return submitDirect(message)
  }, [submitDirect, chatId])

  const classicTripMigrationToken = useMemo(() => {
    return new URLSearchParams(search).get(CLASSIC_TRIP_MIGRATION_QUERY_PARAM)
  }, [search])

  useEffect(() => {
    if (
      !isNewChatRoute ||
      isLoading ||
      isStreaming ||
      !classicTripMigrationToken ||
      consumedClassicTripMigrationTokenRef.current === classicTripMigrationToken
    ) {
      return
    }

    const migrationPrompt = consumeClassicTripMigrationPrompt(classicTripMigrationToken)
    if (migrationPrompt) {
      consumedClassicTripMigrationTokenRef.current = classicTripMigrationToken
      handleSubmitWithTracking(migrationPrompt)
    }
  }, [
    classicTripMigrationToken,
    handleSubmitWithTracking,
    isLoading,
    isNewChatRoute,
    isStreaming,
  ])

  const handleAddTags = useCallback((tags: Array<string>) => {
    const deduplicated = tags.filter(tag => !activeMultiChoiceSelection.includes(tag))
    if (deduplicated.length > 0) {
      handleMultiChoiceSelectionChange([...activeMultiChoiceSelection, ...deduplicated])
    }
  }, [handleMultiChoiceSelectionChange, activeMultiChoiceSelection])

  const handleBookingItemsAdded = useCallback((bookingItemIds: Array<string>) => {
    if (bookingItemIds.length === 0) {
      return
    }

    setBookingSelection((previous) => ({
      ...previous,
      itemIds: [...new Set([...previous.itemIds, ...bookingItemIds])],
    }))
  }, [setBookingSelection])

  const landingPageTags = useMemo(() => {
    return [...new Set([...prefilledOrderChipLabels, ...activeMultiChoiceSelection])]
  }, [activeMultiChoiceSelection, prefilledOrderChipLabels])

  const handleLandingPageTagRemove = useCallback((tag: string) => {
    if (prefilledOrderChipLabels.includes(tag)) {
      onRemovePrefilledOrderTag(tag)
      return
    }

    handleRemoveTag(tag)
  }, [handleRemoveTag, onRemovePrefilledOrderTag, prefilledOrderChipLabels])

  // ── Chat navigation ──

  const resetChatSpecificState = useCallback(() => {
    eventBus.dispatch({ type: 'reset_chat_state' })
    // Reset orchestrator-local state
    resetSelectionState()
    setHoveredHotelId(undefined)
    isCardHoveredRef.current = false
  }, [eventBus, resetSelectionState])

  const resetConversationState = useCallback((options?: ChatNavigationOptions) => {
    syncChatId(undefined, { shouldUpdateUrl: options?.shouldUpdateUrl })
    resetConversation({ followTail: false })
    resetChatSpecificState()
  }, [syncChatId, resetConversation, resetChatSpecificState])

  const handleStartNewChat = useCallback(() => {
    resetConversationState({ shouldUpdateUrl: false })
    navigateToNewChatRoute()
  }, [navigateToNewChatRoute, resetConversationState])

  const loadChatById = useCallback((selectedChatId: number, options?: ChatNavigationOptions) => {
    if (selectedChatId === undefined || selectedChatId === chatId) {
      return
    }

    resetChatSpecificState()
    resetStreamingState()
    chatIdRef.current = selectedChatId
    syncChatId(selectedChatId, { shouldUpdateUrl: options?.shouldUpdateUrl })
    setFollowTail(false)
  }, [
    chatId,
    setFollowTail,
    syncChatId,
    resetChatSpecificState,
    resetStreamingState,
  ])

  const handleSelectChatFromHistory = useCallback((selectedChatId: number) => {
    loadChatById(selectedChatId)
  }, [loadChatById])

  // ── Route change effect ──

  useEffect(() => {
    if (isNewChatRoute) {
      if (chatId) {
        resetConversationState({ shouldUpdateUrl: false })
      }
      return
    }

    if (existingRouteChatId !== undefined) {
      if (existingRouteChatId !== chatId) {
        loadChatById(existingRouteChatId, { shouldUpdateUrl: false })
      }
      return
    }

    if (isLandingRoute && chatId) {
      resetConversationState({ shouldUpdateUrl: false })
    }
  }, [isNewChatRoute, existingRouteChatId, isLandingRoute, chatId, loadChatById, resetConversationState])

  // ── Chat deleted handler (navigation only — header owns the delete flow) ──

  const handleChatDeleted = useCallback((deletedChatId: number, fallbackChatId: number | undefined) => {
    if (chatId === deletedChatId) {
      if (fallbackChatId) {
        loadChatById(fallbackChatId)
      } else {
        resetConversationState()
      }
    }
  }, [chatId, loadChatById, resetConversationState])

  // ── Header ──

  const tripPlannerHeader = useMemo(() => (
    <TripPlannerHeader
      onStartNewChat={handleStartNewChat}
      onChatSelect={handleSelectChatFromHistory}
      onChatDeleted={handleChatDeleted}
      isDevMode={isDevMode}
      onDevModeToggle={onDevModeToggle}
    />
  ), [handleStartNewChat, handleSelectChatFromHistory, handleChatDeleted, isDevMode, onDevModeToggle])

  // ── Render ──

  const hasMessages = messages.length > 0
  const isLandingExperience = isLandingRoute && !hasMessages
  const isNewChatExperience = isNewChatRoute && !hasMessages
  const shouldShowNewChatComposer = isNewChatExperience

  // Landing page or new chat — identical UI
  if (isLandingExperience || shouldShowNewChatComposer) {
    return (
      <MainLayout>
        {tripPlannerHeader}
        <LandingPageWrapper>
          <ChatLandingPage
            onSubmit={handleSubmitWithTracking}
            onSubmitDirect={submitDirectWithTracking}
            onStaffChatOpen={loadChatById}
            onAddTags={handleAddTags}
            disabled={isLoading}
            selectedTags={landingPageTags}
            onRemoveTag={handleLandingPageTagRemove}
            onBookingSelectionChange={setBookingSelection}
            onBookingItemsAdded={handleBookingItemsAdded}
          />
        </LandingPageWrapper>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      {tripPlannerHeader}
      <MapInteractionProvider value={mapInteractionValue}>
        <DrawerAskAIProvider
          submitMessage={submitMessage}
          messages={messages}
          isStreaming={isStreaming}
        >
          <ContentWrapper>
            <MainContent
              ref={mainContentRef}
              style={{ [LEFT_PANE_WIDTH_VAR]: `${leftPaneWidth.current}px` }}
            >
              <ChatColumnLayout>
                <ChatContainer
                  chatId={chatId}
                  isDevMode={isDevMode}
                  isSpoofed={isSpoofed}
                  spoofingAgentEmail={spoofingAgentEmail}
                  navigateToNewChatRoute={navigateToNewChatRoute}
                  handleSubmit={handleSubmitWithTracking}
                  handleMultiChoiceSelectionChange={handleMultiChoiceSelectionChange}
                  activeMultiChoiceSelection={activeMultiChoiceSelection}
                  mobileSelectionText={mobileSelectionText}
                  offerContextTag={offerContextTag}
                  handleRemoveTag={handleRemoveTag}
                  selectedBookingItemIds={selectedBookingItemIds}
                  onBookingItemsAdded={handleBookingItemsAdded}
                  chatInputRef={chatInputRef}
                />
              </ChatColumnLayout>
              <PaneResizer onMouseDown={handlePaneDragStart} />
              <RightPanelContainer />
              <DrawerContainer />
            </MainContent>
          </ContentWrapper>
        </DrawerAskAIProvider>
      </MapInteractionProvider>
    </MainLayout>
  )
}

export default ChatPageContent
